import React, { FC } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Skeleton } from '@mui/material';
import { useGridApiContext, gridColumnPositionsSelector, GridDimensions } from '@mui/x-data-grid';
import { GridStateColDef } from '@mui/x-data-grid/internals';

const SkeletonCell = styled(Box)(({ theme }) => {
    const {
        colours: {
            backgrounds: { backgroundAlt, background }
        },
    } = theme;

    const ROW_BACKGROUND_COLOR: `#${string}` = background;
    const EVEN_ROW_ALT_COLOR: `#${string}` = backgroundAlt;

    return {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '0px 16px',
        '& .MuiSkeleton-root': {
            backgroundColor: ROW_BACKGROUND_COLOR
        },
        '&.even': {
            '& .MuiSkeleton-root': {
                backgroundColor: EVEN_ROW_ALT_COLOR
            }
        }
    }
}
);
interface SkeletonCellContainerProps {
    columns?: GridStateColDef[],
    rowHeight: number,
    totalWidth?: number
}

type SkeletonVariant = 'text' | 'rectangular' | 'rounded' | 'circular';

const SkeletonCellContainer = styled('div')(({ theme }) => {
    return {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    }
});

const SkeletonRowContainer = styled('div', {
    shouldForwardProp: (prop) => prop !== 'rowHeight' && prop !== 'totalWidth'
})<SkeletonCellContainerProps>(({ theme, columns, rowHeight, totalWidth }) => {
    const {
        colours: {
            backgrounds: { backgroundAlt, background },

        },
    } = theme;

    const ROW_BACKGROUND_COLOR: `#${string}` = background;
    const EVEN_ROW_ALT_COLOR: `#${string}` = backgroundAlt;

    return {
        display: 'flex',
        alignItems: 'center',
        width: totalWidth,
        height: rowHeight,
        gap: '8px',
        '&.evenRow': {
            backgroundColor: ROW_BACKGROUND_COLOR,
            '& .MuiSkeleton-root': {
                backgroundColor: EVEN_ROW_ALT_COLOR
            }
        },
        '&.oddRow': {
            backgroundColor: EVEN_ROW_ALT_COLOR,
            '& .MuiSkeleton-root': {
                backgroundColor: ROW_BACKGROUND_COLOR
            }
        },
    }
});

const AESkeletonLoadingOverlay: FC<any> = () => {
    const apiRef = useGridApiContext();
    const dimensions: GridDimensions | null = apiRef.current?.getRootDimensions();

    const viewportHeight: number = dimensions?.viewportOuterSize.height ?? 560;
    const rowHeight: number = 56;
    const skeletonRowsCount: number = Math.ceil(viewportHeight / rowHeight);
    const totalWidth: number = dimensions?.viewportInnerSize.width ?? 0;
    const positions: number[] = gridColumnPositionsSelector(apiRef);
    const inViewportCount: number = React.useMemo(
        () => positions.filter((value) => value <= totalWidth).length,
        [totalWidth, positions],
    );
    const columns: GridStateColDef[] = apiRef.current.getAllColumns().slice(0, inViewportCount);

    const createSkeletonCell = (column: GridStateColDef, rowIndex: number) => {
        const isCheckBox: boolean = column.field === '__check__';
        const variant: SkeletonVariant = isCheckBox ? 'circular' : 'text';
        const width: number | undefined = column.computedWidth;
        const height: number = isCheckBox ? 26 : 10;

        return (
            <SkeletonCell
                key={`col-${column.field}-${rowIndex}`}
                sx={{ justifyContent: column.align }}
                className={rowIndex % 2 === 0 ? 'even' : 'odd'}
            >
                <Skeleton variant={variant} width={width} height={height} />
            </SkeletonCell>
        );
    };

    const createSkeletonRow = (rowIndex: number, columnsData: GridStateColDef[], selectedRowHeight: number, totalWidthOfRow: number) => {
        const columnElements: React.JSX.Element[] = columnsData.map((column) => createSkeletonCell(column, rowIndex));

        return (
            <SkeletonRowContainer
                key={`row-${rowIndex}`}
                columns={[]}
                rowHeight={selectedRowHeight}
                totalWidth={totalWidthOfRow}
                className={rowIndex % 2 === 0 ? 'evenRow' : 'oddRow'}
            >
                {columnElements}
            </SkeletonRowContainer>
        );
    };

    const useSkeletonRows = (createSkeletonRowsCount: number, createSkeletonColumns: GridStateColDef[], createSkeletonRowHeight: number, createSkeletonTotalWidth: number) => {
        return React.useMemo(() => {
            return Array.from({ length: createSkeletonRowsCount }, (_, rowIndex) =>
                createSkeletonRow(rowIndex, createSkeletonColumns, createSkeletonRowHeight, createSkeletonTotalWidth)
            );
        }, [createSkeletonRowsCount, createSkeletonColumns, createSkeletonRowHeight, createSkeletonTotalWidth]);
    };

    const childrenRow: React.JSX.Element[] = useSkeletonRows(skeletonRowsCount, columns, rowHeight, totalWidth);

    return (
        <SkeletonCellContainer data-testid="ae-data-table-skeleton-loader">
            {childrenRow}
        </SkeletonCellContainer>
    );
}

export default AESkeletonLoadingOverlay;